import APIService from "./APIService";
import pollForResult from "../../../utils/pollForResult";

/**
 * Social Media Scam API Service
 * Handles operations for social media scam analysis
 */
class SocialMediaAnalysisAPI {
  /**
   * Submit a social media analysis request (returns immediately with analysisId).
   * The backend processes the AI analysis asynchronously.
   * @param {Object} request - The social media scam analysis request
   * @returns {Promise<Object>} Promise that resolves to { analysisId, status }
   */
  static async submitAnalysis(request) {
    try {
      console.log(
        "SocialMediaAnalysisAPI: Submitting social media analysis:",
        request,
      );

      const response = await APIService.post(
        "/api/v1/scam/detector/social-media/analyze",
        request,
      );

      console.log("SocialMediaAnalysisAPI: Analysis submitted:", response);
      return response;
    } catch (error) {
      console.error(
        "SocialMediaAnalysisAPI: Error submitting social media analysis:",
        error,
      );
      throw error;
    }
  }

  /**
   * Poll for the status/result of an async analysis.
   * @param {string} analysisId - The analysis ID returned by submitAnalysis
   * @returns {Promise<Object>} Promise that resolves to { status, result? }
   */
  static async checkAnalysisStatus(analysisId) {
    try {
      const response = await APIService.get(
        `/api/v1/scam/detector/social-media/status/${encodeURIComponent(analysisId)}`,
      );
      return response;
    } catch (error) {
      console.error(
        "SocialMediaAnalysisAPI: Error checking analysis status:",
        error,
      );
      throw error;
    }
  }

  /**
   * Submit analysis and poll until completion.
   * Returns the full analysis result once ready.
   * @param {Object} request - The social media scam analysis request
   * @param {Object} options - Polling options
   * @param {number} options.pollInterval - Interval between polls in ms (default: 3000)
   * @param {number} options.maxPolls - Maximum number of polls (default: 30 = 90s)
   * @param {function} options.onStatusUpdate - Optional callback for status updates
   * @returns {Promise<Object>} Promise that resolves to the analysis result
   */
  static async analyzeSocialMediaScam(request, options = {}) {
    const {
      pollInterval = 3000,
      maxPolls = 30,
      onStatusUpdate = null,
    } = options;

    // Step 1: Submit the analysis
    const submitResponse = await SocialMediaAnalysisAPI.submitAnalysis(request);
    const analysisId = submitResponse.analysisId;

    if (!analysisId) {
      throw new Error("No analysisId returned from server");
    }

    console.log(
      "SocialMediaAnalysisAPI: Polling for result, analysisId:",
      analysisId,
    );

    // Step 2: Poll for completion using shared utility
    return pollForResult(
      async (pollCount) => {
        const statusResponse =
          await SocialMediaAnalysisAPI.checkAnalysisStatus(analysisId);

        if (onStatusUpdate) {
          onStatusUpdate(statusResponse.status, pollCount);
        }

        if (statusResponse.status === "COMPLETED" && statusResponse.result) {
          console.log(
            "SocialMediaAnalysisAPI: Analysis completed after",
            pollCount,
            "polls",
          );
          return { done: true, result: statusResponse.result };
        }

        if (statusResponse.status === "FAILED") {
          throw new Error(
            statusResponse.message || "Analysis failed on the server",
          );
        }

        return { done: false };
      },
      {
        interval: pollInterval,
        maxPolls,
        timeoutMessage:
          "Analysis timed out. Please check your history in a moment.",
      },
    );
  }

  /**
   * Get list of previous social media scam analyses for a user
   * @param {number} limit - Maximum number of results to return
   * @returns {Promise} Promise that resolves to list of analysis records
   */
  static async getAnalysisHistory(limit = 10) {
    try {
      console.log("SocialMediaAnalysisAPI: Getting analysis history");

      const response = await APIService.get(
        "/api/v1/scam/analysis/social-media/list",
        {
          params: { maxResults: limit },
        },
      );

      console.log(
        "SocialMediaAnalysisAPI: Found",
        response.length,
        "history records",
      );
      return response;
    } catch (error) {
      console.error(
        "SocialMediaAnalysisAPI: Error getting analysis history:",
        error,
      );
      throw error;
    }
  }

  /**
   * Get a specific social media scam analysis by ID
   * @param {string} analysisKey - The analysis key
   * @returns {Promise} Promise that resolves to the analysis result
   */
  static async getAnalysisById(analysisKey) {
    try {
      console.log(
        "SocialMediaAnalysisAPI: Getting analysis by key:",
        analysisKey,
      );

      const response = await APIService.get(
        `/api/v1/scam/analysis/social-media?analysisKey=${encodeURIComponent(
          analysisKey,
        )}`,
      );

      console.log("SocialMediaAnalysisAPI: Analysis retrieved:", response);
      return response;
    } catch (error) {
      console.error(
        "SocialMediaAnalysisAPI: Error getting analysis by key:",
        error,
      );
      throw error;
    }
  }

  /**
   * Delete a social media scam analysis
   * @param {string} analysisKey - The analysis key to delete
   * @returns {Promise} Promise that resolves when deletion is complete
   */
  static async deleteAnalysis(analysisKey) {
    try {
      console.log("SocialMediaAnalysisAPI: Deleting analysis:", analysisKey);

      // Send the key as a query parameter in the URL
      await APIService.delete(
        `/api/v1/scam/analysis/social-media?key=${encodeURIComponent(
          analysisKey,
        )}`,
      );

      console.log("SocialMediaAnalysisAPI: Analysis deleted successfully");
    } catch (error) {
      console.error("SocialMediaAnalysisAPI: Error deleting analysis:", error);
      throw error;
    }
  }

  /**
   * Get common social media platforms for dropdown
   * @returns {Array} Array of common platform names
   */
  static getSocialMediaPlatforms() {
    return [
      "Facebook",
      "Instagram",
      "Twitter/X",
      "TikTok",
      "LinkedIn",
      "WhatsApp",
      "Telegram",
      "Discord",
      "Snapchat",
      "YouTube",
      "Pinterest",
      "Reddit",
      "Other",
    ];
  }

  /**
   * Get common merchandise types for dropdown
   * @returns {Array} Array of common merchandise types
   */
  static getMerchandiseTypes() {
    return [
      "Electronics",
      "Clothing/Fashion",
      "Jewelry",
      "Home & Garden",
      "Beauty & Health",
      "Toys & Games",
      "Sports & Fitness",
      "Automotive",
      "Books & Media",
      "Gift Cards",
      "Services",
      "Cryptocurrency",
      "Investment/Financial",
      "Travel/Vacation",
      "Event Tickets",
      "Other",
    ];
  }

  /**
   * Get common interaction reasons for dropdown
   * @returns {Array} Array of common interaction reasons
   */
  static getInteractionReasons() {
    return [
      "Saw an advertisement",
      "Reels video",
      "Friend/Family recommendation",
      "Influencer promotion",
      "Contest/Giveaway",
      "Special offer/discount",
      "Direct message received",
      "Group/page invitation",
      "Search result",
      "Sponsored content",
      "Pop-up advertisement",
      "Email follow-up",
      "Customer support contact",
      "Investment opportunity",
      "Job/Work opportunity",
      "Other",
    ];
  }
}

export default SocialMediaAnalysisAPI;
